import { defineStore } from 'pinia'
import { api, apiUrl, errorText, isEnterpriseDisabledError, object, setCsrfToken, type JsonRecord } from './api'

const COLLAPSE_KEY = 'zhiying-menu-collapsed'

function readCollapsed() {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1' } catch { return false }
}

export const useAppStore = defineStore('app', {
  state: () => ({
    session: null as JsonRecord | null,
    sessionReady: false,
    sessionError: '',
    enterprise: {} as JsonRecord,
    enterpriseBlocked: false,
    enterpriseCheckLoading: false,
    enterpriseCheckError: '',
    menuCollapsed: readCollapsed()
  }),
  getters: {
    authenticated: state => !!state.session,
    enterpriseName: state => {
      const config = object(state.enterprise.config)
      return String(config.name ?? state.enterprise.name ?? '').trim() || '企业空间'
    }
  },
  actions: {
    toggleMenu() {
      this.menuCollapsed = !this.menuCollapsed
      try { localStorage.setItem(COLLAPSE_KEY, this.menuCollapsed ? '1' : '0') } catch { /* 隐私模式忽略 */ }
    },
    markEnterpriseDisabled() {
      this.enterpriseBlocked = true
      this.enterpriseCheckLoading = false
      this.enterpriseCheckError = ''
    },
    async refreshEnterprise() {
      try {
        this.enterprise = object(await api.get('/enterprise'))
        this.enterpriseBlocked = false
        this.enterpriseCheckError = ''
        return this.enterprise
      } catch (error) {
        if (isEnterpriseDisabledError(error)) this.markEnterpriseDisabled()
        throw error
      }
    },
    async retryEnterpriseCheck() {
      if (this.enterpriseCheckLoading) return
      this.enterpriseCheckLoading = true
      this.enterpriseCheckError = ''
      try {
        await this.refreshEnterprise()
      } catch (error) {
        if (!isEnterpriseDisabledError(error)) {
          this.enterpriseCheckError = '暂时无法确认企业状态，请稍后重试。'
        }
      } finally {
        this.enterpriseCheckLoading = false
      }
    },
    async checkSession() {
      this.sessionError = ''
      try {
        const payload = object(await api.get('/auth/session', true))
        setCsrfToken(payload.csrf_token)
        this.session = object(payload.user ?? payload.session ?? payload)
        this.enterprise = object(payload.enterprise ?? this.session.enterprise)
        // 登录会话只负责身份；企业显示名称以业务接口为权威，避免不同认证服务返回结构不一致。
        try {
          await this.refreshEnterprise()
        } catch {
          // 企业停用由状态层接管；普通业务接口失败仍保留已认证会话。
        }
      } catch (error) {
        this.session = null
        if (isEnterpriseDisabledError(error)) this.markEnterpriseDisabled()
        else if (!(error instanceof Error && 'status' in error && error.status === 401)) this.sessionError = errorText(error)
      } finally { this.sessionReady = true }
    },
    login() {
      const returnTo = `${window.location.origin}/overview`
      window.location.assign(`${apiUrl('/auth/login')}?return_to=${encodeURIComponent(returnTo)}`)
    },
    async logout() {
      try { await api.post('/auth/logout') } finally {
        this.session = null
        this.enterprise = {}
        this.enterpriseBlocked = false
        this.enterpriseCheckLoading = false
        this.enterpriseCheckError = ''
        this.sessionReady = true
        window.location.assign('/overview')
      }
    }
  }
})
